import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  isMainPage?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isMainPage = false }) => {
  const linkPrefix = isMainPage ? "#" : "/#";
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    if (isMainPage) {
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -70% 0px', // Adjust these values to trigger active state earlier/later
        threshold: 0
      };

      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);
      const sections = ['about', 'who-we-serve', 'services', 'contact'];

      sections.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.observe(element);
      });

      return () => observer.disconnect();
    } else {
      setActiveSection('');
    }
  }, [isMainPage]);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();

    if (isMainPage) {
      // If on main page, scroll to section
      const element = document.getElementById(targetId);
      if (element) {
        const headerOffset = 80; // Updated for smaller header
        const elementPosition = element.offsetTop;
        const offsetPosition = elementPosition - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        // Manually set active section on click to ensure immediate feedback
        setActiveSection(targetId);
      }
    } else {
      // If on different page, navigate to main page and store scroll target
      sessionStorage.setItem('scrollToSection', targetId);
      window.location.href = `/${targetId !== 'home' ? '#' + targetId : ''}`;
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Always navigate to home page and scroll to top
    window.location.href = '/';
  };

  const getLinkStyle = (isActive: boolean) => ({
    fontSize: '1.25rem',
    fontWeight: 500,
    padding: '0.5rem 0.5rem',
    borderBottom: isActive ? '2px solid var(--primary-color)' : '2px solid transparent',
    transition: 'all 0.3s ease'
  });

  return (
    <nav
      className="navbar navbar-expand-lg navbar-dark bg-dark fixed-top"
      style={{
        padding: '0.5rem 0',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)'
      }}
    >
      <div className="container-fluid" style={{
        paddingLeft: 'clamp(1rem, 5vw, 8rem)',
        paddingRight: 'clamp(1rem, 5vw, 8rem)',
        maxWidth: '100%'
      }}>
        <a
          className="navbar-brand d-flex align-items-center"
          href="/"
          onClick={handleLogoClick}
          style={{ marginRight: 'auto' }}
        >
          <img
            src="/images/logo/OBM_logo_wtext_horizontal.png"
            alt="Ocean BioMetrics Logo"
            className="logo d-none d-lg-block"
            style={{ height: '40px' }}
          />
          <img
            src="/images/logo/OBM_logo_wtext.svg"
            alt="Ocean BioMetrics Logo"
            className="logo d-block d-lg-none"
            style={{ height: '45px' }}
          />
        </a>

        <button
          className="navbar-toggler ms-auto"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav" style={{ flexGrow: 0, flexShrink: 0 }}>
          <ul className="navbar-nav" style={{ gap: '0.2rem', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            <li className="nav-item">
              <a
                className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
                href={linkPrefix + "about"}
                onClick={(e) => handleSmoothScroll(e, 'about')}
                style={getLinkStyle(isMainPage && activeSection === 'about')}
              >
                About
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeSection === 'who-we-serve' ? 'active' : ''}`}
                href={linkPrefix + "who-we-serve"}
                onClick={(e) => handleSmoothScroll(e, 'who-we-serve')}
                style={getLinkStyle(isMainPage && activeSection === 'who-we-serve')}
              >
                Who We Serve
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeSection === 'services' ? 'active' : ''}`}
                href={linkPrefix + "services"}
                onClick={(e) => handleSmoothScroll(e, 'services')}
                style={getLinkStyle(isMainPage && activeSection === 'services')}
              >
                What We Offer
              </a>
            </li>
            <li className="nav-item">
              <Link
                className={`nav-link ${location.pathname === '/solutions' ? 'active' : ''}`}
                to="/solutions"
                style={getLinkStyle(location.pathname === '/solutions')}
              >
                Solutions
              </Link>
            </li>
            <li className="nav-item">
              <Link
                className={`nav-link ${location.pathname === '/data-apps' ? 'active' : ''}`}
                to="/data-apps"
                style={getLinkStyle(location.pathname === '/data-apps')}
              >
                Resources
              </Link>
            </li>
            <li className="nav-item">
              <Link
                className={`nav-link ${location.pathname === '/team' ? 'active' : ''}`}
                to="/team"
                style={getLinkStyle(location.pathname === '/team' || location.pathname.startsWith('/team/'))}
              >
                Team
              </Link>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                href={linkPrefix + "contact"}
                onClick={(e) => handleSmoothScroll(e, 'contact')}
                style={getLinkStyle(isMainPage && activeSection === 'contact')}
              >
                Contact
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;